import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useAnnotationFullscreen } from '@/features/comment-on-file';
import { assetViewerTransition, getAssetThumbLayoutId } from '@/shared/constants/motion.constants';
import type { AssetFile } from '../../../assetDetail.types';
import { FullscreenToggle } from '../../Fullscreen';
import { ControlGroup, ControlGroupSeparator, HoverControlsPill } from '../../HoverControlsPill';
import { ZoomControls } from '../../ZoomControls';
import { useMediaBox } from '../hooks/useMediaBox';
import { usePersistedEntries } from '../hooks/usePersistedEntries';
import { usePreviewPanGestures } from '../hooks/usePreviewPanGestures';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  usePreviewIsPanning,
  usePreviewPan,
  usePreviewZoom,
  usePreviewZoomReset,
} from '../state/preview-zoom.store';
import { MinimapPreview } from './MinimapPreview';
import { ShapeOverlay } from './ShapeOverlay';

// eslint-disable-next-line i18next/no-literal-string -- annotation mode discriminator
const IMAGE_MODE = { kind: 'image' as const };

interface Props {
  file: AssetFile;
  /** When false, suppress ShapeOverlay (no draft drawing, no persisted shapes). */
  annotationsEnabled?: boolean;
  /** When false, hide the FullscreenToggle in the hover controls pill. */
  showFullscreen?: boolean;
}

export function ImageOverlay({ file, annotationsEnabled = true, showFullscreen = true }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const { box, onLoad } = useMediaBox(imgRef);
  const persisted = usePersistedEntries(IMAGE_MODE);
  const { isFullscreen } = useAnnotationFullscreen();
  const shouldReduceMotion = useReducedMotion();

  const { zoom, zoomIn, zoomOut } = usePreviewZoom();
  const { pan } = usePreviewPan();
  const reset = usePreviewZoomReset();
  const isPanning = usePreviewIsPanning();
  const canZoomOut = zoom > MIN_ZOOM + 1e-6;
  const canZoomIn = zoom < MAX_ZOOM - 1e-6;
  const canReset = zoom !== 1 || pan.x !== 0 || pan.y !== 0;

  useEffect(() => {
    reset();
  }, [file.id, reset]);

  // Pan is gated on zoom only — even with an annotation tool active, the user
  // can still pan via right-click drag, middle-click drag, or Space+drag.
  // `useShapeDrawing` cooperatively bails out of drawing for those triggers so
  // the gesture bubbles to this container.
  const panEnabled = zoom > 1;
  const gestures = usePreviewPanGestures({
    containerRef,
    mediaRef: imgRef,
    enabled: panEnabled,
  });

  // eslint-disable-next-line i18next/no-literal-string -- CSS cursor keywords
  const cursor = isPanning ? 'grabbing' : panEnabled ? 'grab' : undefined;

  return (
    <div className="group relative flex min-h-0 min-w-0 flex-1 flex-col rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background p-4">
      <div
        ref={containerRef}
        className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden"
        style={{ cursor }}
        onPointerDown={gestures.onPointerDown}
        onPointerMove={gestures.onPointerMove}
        onPointerUp={gestures.onPointerUp}
        onPointerCancel={gestures.onPointerCancel}
        onContextMenu={gestures.onContextMenu}
      >
        {/* h-full w-full (not max-h-full / max-w-full) so the img inside has a
            sized containing block — percentage max-height on the img only
            resolves when its parent has a definite height. Without this, the
            img falls back to natural pixel size and overflows vertically. */}
        <div
          className="relative flex h-full w-full items-center justify-center"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isPanning ? 'none' : 'transform 120ms ease-out',
            willChange: 'transform',
          }}
        >
          {/* `motion.img` + per-file `layoutId` morphs the grid thumbnail INTO
              this image on open (). `layout="preserve-aspect"` animates
              BOTH size and position so the thumbnail visibly EXPANDS into the
              viewer (the ticket's headline effect) while motion crossfades the
              scale correction — that's what keeps the differing object-contain
              aspect boxes (small grid cell vs full viewer) from stretching
              mid-flight. (`layout="position"` looked nearly static: it slid the
              already-full-size image a few px instead of expanding it.) The id
              sits on the img, NOT the pan/zoom parent, so motion's layout
              transform never fights the inline translate/scale above. */}
          <motion.img
            ref={imgRef}
            layoutId={shouldReduceMotion ? undefined : getAssetThumbLayoutId(file.id)}
            layout="preserve-aspect"
            transition={assetViewerTransition.morphSpring}
            src={file.downloadUrl ?? undefined}
            alt={file.fileName}
            className="max-h-full max-w-full object-contain"
            onLoad={onLoad}
          />
          {annotationsEnabled && <ShapeOverlay box={box} persisted={persisted} />}
        </div>
        <MinimapPreview file={file} containerRef={containerRef} imageBox={box} />
      </div>
      {!isFullscreen && (
        <HoverControlsPill className="absolute bottom-4 left-4">
          <ControlGroup>
            <ZoomControls
              zoom={zoom}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
              canReset={canReset}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={reset}
            />
            {showFullscreen && (
              <>
                <ControlGroupSeparator />
                <FullscreenToggle />
              </>
            )}
          </ControlGroup>
        </HoverControlsPill>
      )}
    </div>
  );
}
